'use client';

import { useSocketIo } from '@/context/socket-io.context';
import type { ReviewAiJobProgressEventEntity } from '@/entities/review-ai-job-progress-event.entity';
import type { GenerateAiAnswerResponse } from '@/repositories/marketplace-reviews.repository';
import {
    getReviewAiJobMessages,
    resolveReviewAiJobTerminalMessage,
    type ReviewAiJobMessageLocale,
} from '@/shared/review-ai-job-messages';
import { useCallback } from 'react';
import type { AxiosResponse } from 'axios';

export type EnqueueReviewAiAnswerJobContext = {
    signal?: AbortSignal;
};

export type EnqueueReviewAiAnswerJob = (
    operationId: string,
    context: EnqueueReviewAiAnswerJobContext,
) => Promise<AxiosResponse<GenerateAiAnswerResponse>>;

type RunReviewAiAnswerJobOptions = {
    operationId: string;
    enqueue: EnqueueReviewAiAnswerJob;
    locale?: ReviewAiJobMessageLocale;
    signal?: AbortSignal;
};

type ReviewAiJobCompletionWaiter = {
    promise: Promise<ReviewAiJobProgressEventEntity>;
    cancel: () => void;
};

export class ReviewAiJobTerminalError extends Error {
    readonly terminalEvent: ReviewAiJobProgressEventEntity;
    readonly errorCode: string | null;
    readonly errorReason: string | null;
    readonly errorMessage: string | null;

    constructor(event: ReviewAiJobProgressEventEntity, message: string) {
        super(message);
        this.name = 'ReviewAiJobTerminalError';
        this.terminalEvent = event;
        this.errorCode = event.errorCode ?? null;
        this.errorReason = event.errorReason ?? null;
        this.errorMessage = event.errorMessage;
    }
}

const isTerminalReviewAiJobEvent = (event: ReviewAiJobProgressEventEntity): boolean =>
    event.stage === 'completed' || event.stage === 'failed' || event.stage === 'canceled';

const throwIfAborted = (signal?: AbortSignal): void => {
    if (!signal?.aborted) {
        return;
    }

    throw new DOMException('Review AI answer job wait was canceled.', 'AbortError');
};

const waitForAbort = (signal?: AbortSignal): Promise<never> | null => {
    if (!signal) {
        return null;
    }

    return new Promise<never>((_resolve, reject) => {
        const rejectAbort = () => {
            reject(new DOMException('Review AI answer job wait was canceled.', 'AbortError'));
        };

        if (signal.aborted) {
            rejectAbort();
            return;
        }

        signal.addEventListener('abort', rejectAbort, { once: true });
    });
};

export const useRunReviewAiAnswerJob = () => {
    const { events } = useSocketIo();

    const createReviewAiJobCompletionWaiter = useCallback(
        (operationId: string): ReviewAiJobCompletionWaiter => {
            let unsubscribe: () => void = () => undefined;
            const promise = new Promise<ReviewAiJobProgressEventEntity>((resolve) => {
                unsubscribe = events.on('reviewAiJobProgress', (event) => {
                    if (event.operationId !== operationId || !isTerminalReviewAiJobEvent(event)) {
                        return;
                    }

                    unsubscribe();
                    resolve(event);
                });
            });

            return {
                promise,
                cancel: () => unsubscribe(),
            };
        },
        [events],
    );

    return useCallback(
        async ({ operationId, enqueue, locale = 'ru', signal }: RunReviewAiAnswerJobOptions): Promise<string> => {
            const messages = getReviewAiJobMessages(locale);
            let completionWaiter = createReviewAiJobCompletionWaiter(operationId);

            try {
                throwIfAborted(signal);
                const response = await enqueue(operationId, { signal });
                const resolvedOperationId = response.data.operationId?.trim() || operationId;
                if (resolvedOperationId !== operationId) {
                    completionWaiter.cancel();
                    completionWaiter = createReviewAiJobCompletionWaiter(resolvedOperationId);
                }

                throwIfAborted(signal);
                const abortPromise = waitForAbort(signal);
                const terminalEvent = await (abortPromise
                    ? Promise.race([completionWaiter.promise, abortPromise])
                    : completionWaiter.promise);

                if (terminalEvent.stage !== 'completed') {
                    throw new ReviewAiJobTerminalError(
                        terminalEvent,
                        resolveReviewAiJobTerminalMessage({ event: terminalEvent, locale }),
                    );
                }

                const answer = terminalEvent.answer?.trim() || '';
                if (!answer) {
                    throw new Error(messages.emptyAnswer);
                }

                return answer;
            } finally {
                completionWaiter.cancel();
            }
        },
        [createReviewAiJobCompletionWaiter],
    );
};
